/**
 * PrivateInfo (subset) — aligned's `UserAccountPrivate`, at
 * `user_accounts/{userAccountId}/private/info` (see the PII-in-private-
 * subcollection convention). Models the contact/address/demographics/
 * verification/Stripe/payment-routing/resume/LinkedIn/history/self-reported/
 * NDA fields consumed by the Settings screens (Personal, Professional,
 * Payments, NDA), Tax Docs, and admin expert profile tabs. Not modelled:
 * OAuth/redirect tokens (internal to specific auth flows, added per-feature if
 * needed). Dates are ISO strings. `googleDriveEmail` is beta-only — aligned's
 * model has no counterpart; see its field comment.
 */
export interface PublicationVenue {
  name: string;
  tier: string;
  count: number;
}

export interface PublicationStanding {
  label: string | null;
  tier: string | null;
  oneLine: string | null;
}

export interface LoginIpAddress {
  ipAddress: string;
  lastUsedAt: string;
  /** IPQualityScore fields are absent on older audit entries. */
  isSuspicious?: boolean | null;
  isAbusive?: boolean | null;
  isVpn?: boolean | null;
  countryCode?: string | null;
  fraudScore?: number | null;
  recentAbuse?: boolean | null;
  frequentAbuser?: boolean | null;
  highRiskAttacks?: boolean | null;
  botStatus?: boolean | null;
  ipQualityScoreCheckedAt?: string | null;
}

export interface PrivateInfo {
  userAccountId: string;
  firebaseId: string;
  unsubscribeToken?: string | null;

  // Contact
  emailSecondary: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  /**
   * Google account used to share Google Drive files with the expert. Beta-only
   * field: aligned's `UserAccountPrivate` has no counterpart. Seeded from the
   * sign-in email when that is a Gmail address (a known-working Google
   * account); otherwise null until the expert supplies one.
   */
  googleDriveEmail: string | null;
  /** Address rejected by the Google Groups sync; advisory feedback, not an access grant. */
  googleDriveEmailRejected?: string | null;

  // Address
  addressCountry: string | null;
  address1: string | null;
  address2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressRegion: string | null;
  addressPostal: string | null;

  // Demographics
  dateOfBirth: string | null;
  age: number | null;
  gender: string | null;
  genderOtherString: string | null;
  race: string | null;

  // Identity verification flags tied to PII
  isVerifiedIdentity: boolean;
  isVerifiedPhone: boolean;

  // Persona screening outcomes, written only by aligned's screening-review
  // functions. `undefined` never screened for this kind; `null` a flagged
  // report awaits review; `false` no match; `true` a reviewer confirmed it.
  watchlistMatch?: boolean | null;
  pepMatch?: boolean | null;
  adverseMediaMatch?: boolean | null;

  // Stripe Connect account
  stripeAccountId: string | null;
  stripeName: string | null;
  stripeCountry: string | null;
  stripeDefaultCurrency: string | null;
  stripeDisabledReason: string | null;
  stripePayoutsEnabled: boolean;
  stripeRequirementsDue: string[];
  stripeRequirementsDeadline: string | null;
  /** OAuth/onboarding token cleared when the last connected account is removed. */
  stripeRedirectToken?: string | null;

  // Payment routing
  paymentsDefaultAlignedPaymentAccountId: string | null;
  paymentsDefaultCountryCode: string | null;
  paymentsDefaultPlatform: string | null;
  paymentsDefaultPlatformAccountId: string | null;
  isVerifiedStripe: boolean;
  isVerifiedPayPal: boolean;
  isVerifiedPayments: boolean;

  // Resume content
  resumeUrl: string | null;
  resumeMarkdown: string | null;
  resumeSummary: string | null;
  detailedSummaryMarkdown: string | null;

  // LinkedIn profile content
  linkedinUsername: string | null;
  linkedinFirstName: string | null;
  linkedinLastName: string | null;
  linkedinEmail: string | null;
  linkedinLocale: string | null;
  linkedinProfileUrl: string | null;
  linkedinProfilePictureUrl: string | null;

  // Identity history
  previousEmails: string[];
  previousPhoneNumbers: string[];
  previousCountries: string[];

  // Sign-in audit history
  loginIpAddresses: LoginIpAddress[];

  // Other/external platform account metadata shown in admin pool rosters.
  otherPlatformEmail?: string | null;
  otherPlatformId?: string | null;
  otherPlatform?: string | null;
  publicationLinks?: string[];
  publicationCount?: number | null;
  publicationCountNaive?: number | null;
  publicationHindex?: number | null;
  publicationDataFetchedAt?: string | null;
  publicationScrapeSource?: string | null;
  publicationPatentCount?: number | null;
  publicationResearchAreasSummary?: string | null;
  publicationTierDistribution?: Record<string, number>;
  publicationTierDistributionRecent?: Record<string, number>;
  publicationTopVenues?: Record<string, number>;
  publicationTopVenuesRecent?: Record<string, number>;
  publicationVenues?: PublicationVenue[];
  publicationVenuesRecent?: PublicationVenue[];
  publicationTopCited?: Record<string, number>;
  publicationStanding?: PublicationStanding | null;
  publicationAuthorStanding?: string | null;
  publicationQualitySummary?: string | null;
  publicationAnalysisAt?: string | null;

  // Self-reported professional info
  selfReportedDegrees: string | null;
  selfReportedProfession: string | null;
  selfReportedExpertise: string | null;
  selfReportedPortfolio: string | null;

  // NDA acceptance record
  ndaAcceptedAt: string | null;
  ndaVersion: string | null;
  ndaSignerType: string | null;
  ndaContractorEntity: string | null;
  ndaIpAddress: string | null;
  ndaRevokedAt: string | null;
}

type Data = Record<string, unknown>;

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const numOrNull = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const bool = (v: unknown): boolean => v === true;
const boolOrNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const dateString = (v: unknown): string | null => {
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  if (v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: unknown }).toDate === 'function') {
    const date = (v as { toDate: () => Date }).toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
  }
  return null;
};
const numberMap = (v: unknown): Record<string, number> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  return Object.entries(v).reduce<Record<string, number>>((acc, [key, value]) => {
    if (typeof value === 'number') acc[key] = value;
    return acc;
  }, {});
};
const publicationVenueList = (v: unknown): PublicationVenue[] => {
  if (!Array.isArray(v)) return [];
  return v.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const data = item as Record<string, unknown>;
    const name = str(data.name);
    if (!name) return [];
    return [
      {
        name,
        tier: str(data.tier) ?? 'unknown',
        count: numOrNull(data.count) ?? 0,
      },
    ];
  });
};
const publicationStanding = (v: unknown): PublicationStanding | null => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const data = v as Record<string, unknown>;
  return {
    label: str(data.label),
    tier: str(data.tier),
    oneLine: str(data.oneLine),
  };
};
const loginIpAddressList = (v: unknown): LoginIpAddress[] => {
  if (!Array.isArray(v)) return [];
  return v
    .flatMap((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
      const data = item as Record<string, unknown>;
      const ipAddress = str(data.ipAddress)?.trim();
      const lastUsedAt = dateString(data.lastUsedAt);
      if (!ipAddress || !lastUsedAt) return [];
      return [
        {
          ipAddress,
          lastUsedAt,
          isSuspicious: boolOrNull(data.isSuspicious),
          isAbusive: boolOrNull(data.isAbusive),
          isVpn: boolOrNull(data.isVpn),
          countryCode: str(data.countryCode)?.trim().toUpperCase() || null,
          fraudScore: numOrNull(data.fraudScore),
          recentAbuse: boolOrNull(data.recentAbuse),
          frequentAbuser: boolOrNull(data.frequentAbuser),
          highRiskAttacks: boolOrNull(data.highRiskAttacks),
          botStatus: boolOrNull(data.botStatus),
          ipQualityScoreCheckedAt: dateString(data.ipQualityScoreCheckedAt),
        },
      ];
    })
    .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
};

/**
 * The screening match fields, keeping the difference between absent (never
 * screened) and null (awaiting review): only fields present on the doc are set.
 */
function screeningMatchFields(data: Data): Pick<PrivateInfo, 'watchlistMatch' | 'pepMatch' | 'adverseMediaMatch'> {
  const out: Pick<PrivateInfo, 'watchlistMatch' | 'pepMatch' | 'adverseMediaMatch'> = {};
  for (const key of ['watchlistMatch', 'pepMatch', 'adverseMediaMatch'] as const) {
    if (!(key in data)) continue;
    const value = data[key];
    out[key] = typeof value === 'boolean' ? value : null;
  }
  return out;
}

export function mapPrivateInfo(_id: string, data: Data): PrivateInfo {
  return {
    userAccountId: str(data.userAccountId) ?? '',
    firebaseId: str(data.firebaseId) ?? '',
    unsubscribeToken: str(data.unsubscribeToken),

    emailSecondary: str(data.emailSecondary),
    phoneCountryCode: str(data.phoneCountryCode),
    phoneNumber: str(data.phoneNumber),
    googleDriveEmail: str(data.googleDriveEmail),
    googleDriveEmailRejected: str(data.googleDriveEmailRejected),

    addressCountry: str(data.addressCountry),
    address1: str(data.address1),
    address2: str(data.address2),
    addressCity: str(data.addressCity),
    addressState: str(data.addressState),
    addressRegion: str(data.addressRegion),
    addressPostal: str(data.addressPostal),

    dateOfBirth: str(data.dateOfBirth),
    age: numOrNull(data.age),
    gender: str(data.gender),
    genderOtherString: str(data.genderOtherString),
    race: str(data.race),

    isVerifiedIdentity: bool(data.isVerifiedIdentity),
    isVerifiedPhone: bool(data.isVerifiedPhone),

    ...screeningMatchFields(data),

    stripeAccountId: str(data.stripeAccountId),
    stripeName: str(data.stripeName),
    stripeCountry: str(data.stripeCountry),
    stripeDefaultCurrency: str(data.stripeDefaultCurrency),
    stripeDisabledReason: str(data.stripeDisabledReason),
    stripePayoutsEnabled: bool(data.stripePayoutsEnabled),
    stripeRequirementsDue: strList(data.stripeRequirementsDue),
    stripeRequirementsDeadline: str(data.stripeRequirementsDeadline),
    stripeRedirectToken: str(data.stripeRedirectToken),

    paymentsDefaultAlignedPaymentAccountId: str(data.paymentsDefaultAlignedPaymentAccountId),
    paymentsDefaultCountryCode: str(data.paymentsDefaultCountryCode),
    paymentsDefaultPlatform: str(data.paymentsDefaultPlatform),
    paymentsDefaultPlatformAccountId: str(data.paymentsDefaultPlatformAccountId),
    isVerifiedStripe: bool(data.isVerifiedStripe),
    isVerifiedPayPal: bool(data.isVerifiedPayPal),
    isVerifiedPayments: bool(data.isVerifiedPayments),

    resumeUrl: str(data.resumeUrl),
    resumeMarkdown: str(data.resumeMarkdown),
    resumeSummary: str(data.resumeSummary),
    detailedSummaryMarkdown: str(data.detailedSummaryMarkdown),

    linkedinUsername: str(data.linkedinUsername),
    linkedinFirstName: str(data.linkedinFirstName),
    linkedinLastName: str(data.linkedinLastName),
    linkedinEmail: str(data.linkedinEmail),
    linkedinLocale: str(data.linkedinLocale),
    linkedinProfileUrl: str(data.linkedinProfileUrl),
    linkedinProfilePictureUrl: str(data.linkedinProfilePictureUrl),

    previousEmails: strList(data.previousEmails),
    previousPhoneNumbers: strList(data.previousPhoneNumbers),
    previousCountries: strList(data.previousCountries),
    loginIpAddresses: loginIpAddressList(data.loginIpAddresses),

    otherPlatformEmail: str(data.otherPlatformEmail),
    otherPlatformId: str(data.otherPlatformId),
    otherPlatform: str(data.otherPlatform),
    publicationLinks: strList(data.publicationLinks),
    publicationCount: numOrNull(data.publicationCount),
    publicationCountNaive: numOrNull(data.publicationCountNaive),
    publicationHindex: numOrNull(data.publicationHindex),
    publicationDataFetchedAt: dateString(data.publicationDataFetchedAt),
    publicationScrapeSource: str(data.publicationScrapeSource),
    publicationPatentCount: numOrNull(data.publicationPatentCount),
    publicationResearchAreasSummary: str(data.publicationResearchAreasSummary),
    publicationTierDistribution: numberMap(data.publicationTierDistribution),
    publicationTierDistributionRecent: numberMap(data.publicationTierDistributionRecent),
    publicationTopVenues: numberMap(data.publicationTopVenues),
    publicationTopVenuesRecent: numberMap(data.publicationTopVenuesRecent),
    publicationVenues: publicationVenueList(data.publicationVenues),
    publicationVenuesRecent: publicationVenueList(data.publicationVenuesRecent),
    publicationTopCited: numberMap(data.publicationTopCited),
    publicationStanding: publicationStanding(data.publicationStanding),
    publicationAuthorStanding: str(data.publicationAuthorStanding),
    publicationQualitySummary: str(data.publicationQualitySummary),
    publicationAnalysisAt: dateString(data.publicationAnalysisAt),

    selfReportedDegrees: str(data.selfReportedDegrees),
    selfReportedProfession: str(data.selfReportedProfession),
    selfReportedExpertise: str(data.selfReportedExpertise),
    selfReportedPortfolio: str(data.selfReportedPortfolio),

    ndaAcceptedAt: str(data.ndaAcceptedAt),
    ndaVersion: str(data.ndaVersion),
    ndaSignerType: str(data.ndaSignerType),
    ndaContractorEntity: str(data.ndaContractorEntity),
    ndaIpAddress: str(data.ndaIpAddress),
    ndaRevokedAt: str(data.ndaRevokedAt),
  };
}

/** aligned `UserAccountPrivate.hasStripeRequirementsDue`. */
export function hasStripeRequirementsDue(info: Pick<PrivateInfo, 'stripeRequirementsDue'>): boolean {
  return info.stripeRequirementsDue.length > 0;
}

/**
 * aligned `UserAccountPrivate.hasAcceptedNda`: true only for a current,
 * non-revoked acceptance — an admin revocation clears it until re-signed.
 */
export function hasAcceptedNda(
  info: Pick<PrivateInfo, 'ndaAcceptedAt' | 'ndaVersion' | 'ndaRevokedAt'>,
): boolean {
  return info.ndaAcceptedAt !== null && info.ndaVersion !== null && info.ndaRevokedAt === null;
}
